import type { FramingSceneView } from "../framingDiagnostics";

/**
 * Failure-first scene ranking for the Framing & crop section (CROP-WB-004).
 *
 * Pure and deterministic: identical inputs always produce identical order.
 * Ties inside a tier keep timeline order (numeric-aware scene id), so equal
 * priorities never visually reorder between renders.
 */

export type FramingScenePriorityTier =
  | "exact_feedback"
  | "delivered_subject_loss"
  | "plan_applied_mismatch"
  | "repair_fallback"
  | "partial_evidence"
  | "timeline";

export const FRAMING_PRIORITY_TIER_ORDER: readonly FramingScenePriorityTier[] =
  [
    "exact_feedback",
    "delivered_subject_loss",
    "plan_applied_mismatch",
    "repair_fallback",
    "partial_evidence",
    "timeline",
  ];

export const FRAMING_PRIORITY_TIER_LABELS: Record<
  FramingScenePriorityTier,
  string
> = {
  exact_feedback: "Crop feedback",
  delivered_subject_loss: "Delivered subject at risk",
  plan_applied_mismatch: "Plan vs applied mismatch",
  repair_fallback: "Repaired / fallback",
  partial_evidence: "Partial or ambiguous evidence",
  timeline: "Timeline order",
};

export type FramingFeedbackSceneSets = {
  /** Scene ids named by a frozen exact feedback snapshot. */
  exact?: ReadonlySet<string>;
  /** Scene ids uniquely derived from current timeline events. */
  derived?: ReadonlySet<string>;
  /** Scene ids that are one of several candidates for one feedback. */
  ambiguous?: ReadonlySet<string>;
};

export type PrioritizedFramingScene = {
  scene: FramingSceneView;
  tier: FramingScenePriorityTier;
  /** Lower ranks first; equals the tier index. */
  score: number;
  /** Human-auditable reasons; never empty for non-timeline tiers. */
  reasons: string[];
  feedbackStatus: "exact" | "derived" | "ambiguous" | null;
};

const DELIVERED_SUBJECT_LOSS_CODES = new Set([
  "identity_mismatch",
  "subject_missing",
  "wrong_subject",
  "protected_subject_missing",
  "subject_edge_cut",
  "semantic_subject_missing",
]);

const PLAN_APPLIED_MISMATCH_CODES = new Set([
  "render_receipt_attempt_mismatch",
  "render_receipt_task_mismatch",
  "render_receipt_plan_mismatch",
  "plan_receipt_mismatch",
  "receipt_hash_mismatch",
]);

function hasDeliveredSubjectLoss(scene: FramingSceneView): boolean {
  if (scene.qc?.status === "failed") return true;
  return (scene.qc?.issue_codes ?? []).some((code) =>
    DELIVERED_SUBJECT_LOSS_CODES.has(code),
  );
}

function hasPlanAppliedMismatch(scene: FramingSceneView): boolean {
  if (
    scene.plannedStrategy != null &&
    scene.appliedStrategy != null &&
    scene.plannedStrategy !== scene.appliedStrategy
  ) {
    return true;
  }
  return (scene.qc?.issue_codes ?? []).some((code) =>
    PLAN_APPLIED_MISMATCH_CODES.has(code),
  );
}

function hasPartialEvidence(scene: FramingSceneView): boolean {
  return (
    scene.qc == null ||
    scene.planDigest == null ||
    scene.attemptId == null ||
    scene.events.length === 0
  );
}

export function framingScenePriority(
  scene: FramingSceneView,
  feedback: FramingFeedbackSceneSets = {},
): Pick<PrioritizedFramingScene, "tier" | "reasons" | "feedbackStatus"> {
  const reasons: string[] = [];
  const exact = feedback.exact?.has(scene.sceneId) ?? false;
  const derived = feedback.derived?.has(scene.sceneId) ?? false;
  const ambiguous = feedback.ambiguous?.has(scene.sceneId) ?? false;
  const feedbackStatus = exact
    ? "exact"
    : derived
      ? "derived"
      : ambiguous
        ? "ambiguous"
        : null;

  if (exact || derived) {
    reasons.push(
      exact
        ? "Exact crop feedback frozen to this scene"
        : "Crop feedback derived from the delivered timeline",
    );
    return { tier: "exact_feedback", reasons, feedbackStatus };
  }
  if (hasDeliveredSubjectLoss(scene)) {
    if (scene.qc?.status === "failed") reasons.push("Final framing QC failed");
    for (const code of scene.qc?.issue_codes ?? []) {
      if (DELIVERED_SUBJECT_LOSS_CODES.has(code)) reasons.push(code);
    }
    return { tier: "delivered_subject_loss", reasons, feedbackStatus };
  }
  if (hasPlanAppliedMismatch(scene)) {
    if (
      scene.plannedStrategy != null &&
      scene.appliedStrategy != null &&
      scene.plannedStrategy !== scene.appliedStrategy
    ) {
      reasons.push(
        `Planned ${scene.plannedStrategy} but applied ${scene.appliedStrategy}`,
      );
    }
    for (const code of scene.qc?.issue_codes ?? []) {
      if (PLAN_APPLIED_MISMATCH_CODES.has(code)) reasons.push(code);
    }
    return { tier: "plan_applied_mismatch", reasons, feedbackStatus };
  }
  if (scene.repaired || scene.fallback) {
    if (scene.repaired) reasons.push("Scene was repaired");
    if (scene.fallback) {
      reasons.push(
        scene.fallbackReason
          ? `Fallback applied: ${scene.fallbackReason}`
          : "Fallback applied",
      );
    }
    return { tier: "repair_fallback", reasons, feedbackStatus };
  }
  if (ambiguous || hasPartialEvidence(scene)) {
    if (ambiguous) {
      reasons.push("Ambiguous feedback candidate (one of several scenes)");
    }
    if (scene.qc == null) reasons.push("No QC evidence recorded");
    if (scene.planDigest == null) reasons.push("No plan digest recorded");
    if (scene.attemptId == null) reasons.push("No attempt identity recorded");
    return { tier: "partial_evidence", reasons, feedbackStatus };
  }
  return { tier: "timeline", reasons, feedbackStatus };
}

function timelineOrder(
  left: FramingSceneView,
  right: FramingSceneView,
): number {
  return left.sceneId.localeCompare(right.sceneId, undefined, {
    numeric: true,
  });
}

/**
 * Rank scenes failure-first with stable timeline tie-breaking. The input
 * array is not mutated.
 */
export function prioritizeFramingScenes(
  scenes: readonly FramingSceneView[],
  feedback: FramingFeedbackSceneSets = {},
): PrioritizedFramingScene[] {
  return scenes
    .map((scene) => {
      const priority = framingScenePriority(scene, feedback);
      return {
        scene,
        tier: priority.tier,
        score: FRAMING_PRIORITY_TIER_ORDER.indexOf(priority.tier),
        reasons: priority.reasons,
        feedbackStatus: priority.feedbackStatus,
      };
    })
    .sort(
      (left, right) =>
        left.score - right.score || timelineOrder(left.scene, right.scene),
    );
}
